/**
 * Is a field engineer an NSC-internal one or a partner's? ServiceNow now says so itself: each row of
 * `engineer_list` carries the engineer's company and two type fields (added 2026-10-14):
 *
 *   { "company": "NSC Global", "is_external_fe": false, "fe_type": "internal" }
 *
 * The export's `Engineer Type` column is taken straight from those fields:
 *
 *   `is_external_fe` is true, or `fe_type` is an external one   → "partner"
 *   `fe_type` is "internal"                                      → "internal"
 *   anything else — `fe_type` "unknown", or neither field sent   → BLANK, and counted as UNKNOWN for review
 *
 * Only "partner" is acted on by the import: it sets the partner flag. "internal" is written for readability, so
 * that a file shows what ServiceNow said instead of an empty column; the import ignores it. A blank cell means
 * ServiceNow could not tell (an engineer with no company, or an instance that does not send the fields yet).
 *
 * `is_external_fe` wins over `fe_type` when it is true. A disagreement between the two (a flag of false with an
 * external `fe_type`, say) is reported by the export rather than guessed at.
 *
 * Pure, so the mapping can be tested without ServiceNow.
 */

/** The value of the column: `partner`, `internal`, or blank when ServiceNow does not say. */
export type EngineerType = 'partner' | 'internal' | '';

/** Why the engineer got the value they did — printed in the export summary so a reviewer can see the gaps. */
export type EngineerTypeBasis = 'partner' | 'internal' | 'unknown';

export interface EngineerTypeResult {
  type: EngineerType;
  basis: EngineerTypeBasis;
  /** `is_external_fe` and `fe_type` contradict each other. The value above follows the rules; it is a flag to review. */
  conflict: boolean;
}

/** The type-bearing part of an `engineer_list` row. */
export interface EngineerTypeFields {
  is_external_fe?: boolean | string | null;
  fe_type?: string | null;
}

/** `true`/`false` from a real boolean or the `"true"`/`"1"` strings ServiceNow sometimes sends; undefined if absent. */
function asBoolean(value: boolean | string | null | undefined): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLocaleLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return undefined;
}

/** What `fe_type` says: internal, an external/partner engineer, or nothing usable ("unknown", blank, new values). */
function feTypeKind(feType: string | null | undefined): 'internal' | 'external' | 'unknown' {
  const normalized = (feType ?? '').trim().toLocaleLowerCase();
  if (normalized === 'internal') return 'internal';
  if (normalized.includes('external') || normalized.includes('partner')) return 'external';
  return 'unknown';
}

export function engineerTypeOf(engineer: EngineerTypeFields): EngineerTypeResult {
  const external = asBoolean(engineer.is_external_fe);
  const kind = feTypeKind(engineer.fe_type);

  if (external === true) return { type: 'partner', basis: 'partner', conflict: kind === 'internal' };
  if (kind === 'external') return { type: 'partner', basis: 'partner', conflict: external === false };
  if (kind === 'internal') return { type: 'internal', basis: 'internal', conflict: false };
  return { type: '', basis: 'unknown', conflict: false };
}
